import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {lookup, fetch} from "../assets/virtual-flyout/waypont-service.js";
import {findAirportByName, getAirportDetails} from "../assets/virtual-flyout/airport-service";
import {getDistance} from "../assets/virtual-flyout/nav-calc.js";

import PredictionTextField from "./PredictionTextField";
FlyoutLeg.propTypes = {
  leg: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  onDelete: PropTypes.func
}

function validate(data) {
  const errors = [];
  if (!data.departureTime) {
    errors.push("Please enter departure time");
  }
  for (let index = 0; index < data.segments.length; index++) {
    const segment = data.segments[index];
    if (!segment.from) {
      errors.push(`Please enter "from" waypoint for segment ${index + 1}`);
    }
    if (!segment.to) {
      errors.push(`Please enter "to" waypoint for segment ${index + 1}`);
    }
    if (!segment.altitude) {
      errors.push(`Please enter altitude for segment ${index + 1}`);
    }
  }
  // Check if the last segment's destination is a valid airport
  if (data.segments.length > 0) {
    const lastSegment = data.segments[data.segments.length - 1];
    if (lastSegment.to) {
      const airport = getAirportDetails(lastSegment.to.code);
      if (!airport) {
        errors.push(`Unknown destination airport ${lastSegment.to.code}`);
      }
    }
  }
  return errors;
}

function isLegEndInAirport(leg) {
  const lastSegment = leg.segments[leg.segments.length - 1];
  return lastSegment.to && lastSegment.to.type === "AD";
}

export default function FlyoutLeg({leg, index, onChange, onDelete}) {

  const [state, setState] = useState();
  const isEditing = state !== "VIEW";
  const [unsavedData, setUnsavedData] = useState(leg);

  useEffect(() => {
    setUnsavedData({...leg, state: "VIEW"});
    setState(leg.state ?? "VIEW");
  }, [leg]);

  const [errors, setErrors] = useState();
  useEffect(() => {
    if (state === "INVALID") {
      setErrors(validate(unsavedData));
    }
  }, [unsavedData, state]);

  function handleEdit() {
    // Create a deep copy while preserving number formatting
    const copy = {...leg};
    copy.segments = leg.segments.map(segment => ({
      ...segment,
      altitude: parseFloat(segment.altitude).toFixed(1)  // Ensure consistent decimal format
    }));
    setUnsavedData(copy);
    setState("EDIT");
  }

  function handleSave() {
    const errors = validate(unsavedData);
    if (!errors || errors.length === 0) {
      onChange(index, unsavedData);
      setState("VIEW");
    } else {
      setState("INVALID");
    }
  }

  function handleDiscard() {
    if (leg.state === "NEW") {
      handleDelete();
    } else {
      setState("VIEW");
    }
    setErrors(null);
  }

  function handleDelete() {
    onDelete(index);
  }

  function handleAddSegment() {
    // append the new segment to the previous one at the same altitude
    const lastSegment = unsavedData.segments[unsavedData.segments.length - 1];
    const newSegments = JSON.parse(JSON.stringify(unsavedData.segments));
    newSegments.push({
      from: lastSegment.to,
      to: null,
      altitude: lastSegment.altitude
    });
    setUnsavedData((prev) => ({...prev, segments: newSegments}));
  }

  function handleDeleteSegment(index) {
    // if not the last segment, update the next one with the from of the deleted segment
    const newSegments = JSON.parse(JSON.stringify(unsavedData.segments));
    if (index < unsavedData.segments.length - 1) {
      newSegments[index + 1].from = unsavedData.segments[index].from;
    }
    newSegments.splice(index, 1);
    setUnsavedData((prev) => ({...prev, segments: newSegments}));
  }

  function handleChangeValue(type, value, index) {
    const newSegments = JSON.parse(JSON.stringify(unsavedData.segments));
    if (type === "to") {
      const fromWaypoint = unsavedData.segments[index].from;
      const waypoint = fetch(value, fromWaypoint);
      
      newSegments[index].to = waypoint;

      // if not the last segment, update the next one with the from of the deleted segment
      if (index < unsavedData.segments.length - 1) {
        newSegments[index + 1].from = waypoint;
      }
    } else if (type === "altitude") {
      newSegments[index].altitude = value;
    }
    setUnsavedData((prev) => ({...prev, segments: newSegments}));
  }

  function formatAirport(airport) {
    return airport ? `${airport.airport_name} (${airport.airport_code})` : "";
  }

  function displayWaypoint(code) {
    if (!code) {
      return null;
    }
    const airport = getAirportDetails(code);
    return airport ? formatAirport(airport) : code;
  }

  function handlePredictionLookup(searchValue, segmentIndex) {
    // Get the "from" waypoint of the current segment
    const fromWaypoint = unsavedData.segments[segmentIndex].from;
    if (!fromWaypoint) {
      return null;
    }

    // Search for matching airports by name
    const airport = findAirportByName(searchValue);
    
    // Search for matching waypoints by code
    const waypoint = lookup(searchValue, fromWaypoint);

    // If we have both matches, compare their distances
    if (airport && waypoint) {
      // if waypoint is a complete match, return the waypoint
      if (waypoint.code.toUpperCase() === searchValue.toUpperCase()) {
        return {
          displayValue: waypoint.code,
          value: waypoint.code
        };
      }

      // Convert airport to waypoint format for distance comparison
      const airportWaypoint = fetch(airport.airport_code);
      if (!airportWaypoint) {
        return {
          displayValue: formatAirport(airport),
          value: airport.airport_code
        };
      }

      // Compare distances using nav-calc's getDistance function
      const airportDist = getDistance(fromWaypoint, airportWaypoint);
      const waypointDist = getDistance(fromWaypoint, waypoint);

      if (airportDist <= waypointDist) {
        return {
          displayValue: formatAirport(airport),
          value: airport.airport_code
        };
      } else {
        return {
          displayValue: waypoint.code,
          value: waypoint.code
        };
      }
    }

    // Return whichever match we found, or null if none
    if (airport) {
      return {
        displayValue: formatAirport(airport),
        value: airport.airport_code
      };
    }
    if (waypoint) {
      return {
        displayValue: waypoint.code,
        value: waypoint.code
      };
    }
    return null;
  }

  return <div key={index} className="flyout-plan-leg">
    <div className="flyout-plan-leg-header">
      <div className="flyout-plan-dep-time">
        <h3>Dep:</h3>
        {!isEditing && <p>{leg.departureTime}</p>}
        {isEditing && <input type="time"
                             className={`flyout-plan-leg-textbox ${!unsavedData.departureTime && errors ?
                               'error' : ''}`}
                             value={unsavedData.departureTime}
                             onChange={(e) => setUnsavedData(
                               (prev) => ({...prev, departureTime: e.target.value}))}/>}
      </div>
      <div className="flyout-plan-leg-buttons">
        {!isEditing &&
          <button className="flyout-plan-leg-header-button" title="Edit" tabIndex="-1"
                  onClick={handleEdit}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                 stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/>
            </svg>
          </button>
        }
        {!isEditing &&
          <button className="flyout-plan-leg-header-button" title="Delete" tabIndex="-1"
                  onClick={handleDelete}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                 stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
            </svg>
          </button>
        }
        {isEditing &&
          <button className="flyout-plan-leg-header-button" title="Save" tabIndex="-1" onClick={handleSave}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                 stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </button>
        }
        {isEditing &&
          <button className="flyout-plan-leg-header-button" title="Cancel" tabIndex="-1" onClick={handleDiscard}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                 stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/>
            </svg>
          </button>
        }
      </div>
    </div>
    <table className="flyout-plan-segments">
      <thead>
      <tr>
        <th>From</th>
        <th>To</th>
        <th>Alt</th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      {(!isEditing ? leg : unsavedData).segments.map((segment, index) => (
        <tr key={index}>
          <td>
            {!isEditing && displayWaypoint(segment.from?.code)}
            {isEditing &&
              <input type="text" value={displayWaypoint(segment.from?.code)} className="flyout-plan-leg-textbox"
                     tabIndex="-1" readOnly="readonly"/>}
          </td>
          <td>
            {!isEditing && displayWaypoint(segment.to?.code)}
            {isEditing && <PredictionTextField 
              className="flyout-plan-leg-textbox" 
              lookupFn={(searchValue) => handlePredictionLookup(searchValue, index)} 
              displayFn={displayWaypoint}
              value={segment.to?.code}
              onChange={(value) => handleChangeValue("to", value, index)}
            />}
          </td>
          <td>
            {!isEditing && parseFloat(segment.altitude).toFixed(1)}
            {isEditing && <input className="flyout-plan-leg-textbox"
                                 autoComplete="off" autoCorrect="off"
                                 value={segment.altitude}
                                 onChange={(e) => {
                                   handleChangeValue("altitude", e.target.value, index);
                                 }}
                                 onKeyDown={(e) => {
                                   if (e.key === "Tab" && !isLegEndInAirport(unsavedData)) {
                                     if (index === unsavedData.segments.length - 1) {
                                       handleAddSegment();
                                     }
                                   }
                                 }}
            />}

          </td>
          <td>
            {isEditing && (index > 0) &&
              <button className="flyout-plan-leg-header-button" title="Delete" tabIndex="-1"
                      onClick={() => handleDeleteSegment(index)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                     stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12"/>
                </svg>
              </button>}
          </td>
        </tr>
      ))}
      </tbody>
    </table>
    <div className="flyout-plan-leg-footer">
      {isEditing &&
        <button className="flyout-plan-leg-footer-button" title="Add Segment" tabIndex="-1" onClick={handleAddSegment}>
          Add Segment
        </button>}
    </div>
    {errors && errors.length > 0 && <ul className="flyout-plan-leg-errors">
      {errors.map((error, index) => (
        <li key={index}>{error}</li>
      ))}
    </ul>}
  </div>
}